import { useCallback, useEffect, useRef, useState } from "react"
import type { PointerEvent as ReactPointerEvent } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import api from "../../lib/api"
import { toastError } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { Modal } from "../../components/ui/modal"
import { inputCls, labelCls, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { Toggle } from "../../components/ui/Toggle"
import { LoadState } from "@/components/ui/LoadState"

type Align = "left" | "center" | "right"

type CertificateField = {
  key: string
  label?: string
  x: number
  y: number
  fontSize: number
  fontWeight?: string
  color: string
  align?: Align
  textTransform?: "none" | "uppercase"
  sampleValue?: string
}

type CertificateTemplate = {
  id: string
  name: string
  backgroundKey: string
  background_url: string
  canvasWidth: number
  canvasHeight: number
  fieldsJson: CertificateField[]
  mode: "individual" | "group"
  seedbedId: string | null
}

const DISPLAY_MAX_WIDTH = 720

const FIELD_PRESETS: { key: string; label: string }[] = [
  { key: "studentName", label: "Nombre del estudiante" },
  { key: "groupName", label: "Nombre del grupo" },
  { key: "courseName", label: "Curso / programa" },
  { key: "issuedDate", label: "Fecha de emisión" },
]

function newField(key: string, label: string, canvasWidth: number, canvasHeight: number): CertificateField {
  return {
    key,
    label,
    x: Math.round(canvasWidth / 2),
    y: Math.round(canvasHeight / 2),
    fontSize: 36,
    color: "#111111",
    align: "center",
    textTransform: "none",
    sampleValue: label,
  }
}

export default function CertificateTemplateEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === "new"
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const presetSeedbedId = searchParams.get("seedbedId")

  const [name, setName] = useState("")
  const [seedbedId, setSeedbedId] = useState<string | null>(presetSeedbedId)
  const [mode, setMode] = useState<"individual" | "group">("individual")
  const [canvasWidth, setCanvasWidth] = useState(1600)
  const [canvasHeight, setCanvasHeight] = useState(1131)
  const [backgroundKey, setBackgroundKey] = useState("")
  const [backgroundUrl, setBackgroundUrl] = useState("")
  const [fields, setFields] = useState<CertificateField[]>([])
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const [loading, setLoading] = useState(!isNew)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)

  const dragState = useRef<{ index: number; startX: number; startY: number; fieldX: number; fieldY: number } | null>(null)
  const canvasRef = useRef<HTMLDivElement>(null)

  const scale = DISPLAY_MAX_WIDTH / canvasWidth
  const displayHeight = canvasHeight * scale

  const load = useCallback(async () => {
    if (isNew || !id) return
    setLoading(true)
    try {
      const res = await api.get(`/academy/certificate-templates/${id}`)
      const t: CertificateTemplate = res.data.data
      setName(t.name)
      setMode(t.mode)
      setCanvasWidth(t.canvasWidth)
      setCanvasHeight(t.canvasHeight)
      setBackgroundKey(t.backgroundKey)
      setBackgroundUrl(t.background_url)
      setFields(t.fieldsJson ?? [])
      setSeedbedId(t.seedbedId)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void load()
  }, [load])

  async function handleFileChange(file: File) {
    setUploading(true)
    try {
      // Read natural dimensions from the file itself, before uploading.
      const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
        img.onerror = () => reject(new Error("No se pudo leer la imagen"))
        img.src = URL.createObjectURL(file)
      })

      const uploadReq = await api.post("/academy/certificate-templates/background-upload-url", {
        filename: file.name,
        content_type: file.type || "image/png",
      })
      const { upload_url, key } = uploadReq.data.data

      const putRes = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type || "image/png" }, body: file })
      if (!putRes.ok) throw new Error(`Error subiendo la imagen: ${putRes.status}`)

      setBackgroundKey(key)
      setBackgroundUrl(URL.createObjectURL(file))
      setCanvasWidth(dims.w)
      setCanvasHeight(dims.h)
    } catch (err) {
      toastError(err)
    } finally {
      setUploading(false)
    }
  }

  function addField(preset: { key: string; label: string }) {
    if (fields.some((f) => f.key === preset.key)) return
    setFields((prev) => [...prev, newField(preset.key, preset.label, canvasWidth, canvasHeight)])
    setSelectedIndex(fields.length)
  }

  function addCustomField() {
    const key = `custom${fields.length + 1}`
    setFields((prev) => [...prev, newField(key, "Campo personalizado", canvasWidth, canvasHeight)])
    setSelectedIndex(fields.length)
  }

  function removeField(index: number) {
    setFields((prev) => prev.filter((_, i) => i !== index))
    setSelectedIndex(null)
  }

  function updateField(index: number, patch: Partial<CertificateField>) {
    setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  function onFieldPointerDown(e: ReactPointerEvent<HTMLDivElement>, index: number) {
    e.preventDefault()
    setSelectedIndex(index)
    dragState.current = { index, startX: e.clientX, startY: e.clientY, fieldX: fields[index].x, fieldY: fields[index].y }
    window.addEventListener("pointermove", onWindowPointerMove)
    window.addEventListener("pointerup", onWindowPointerUp)
  }

  const onWindowPointerMove = useCallback((e: PointerEvent) => {
    const drag = dragState.current
    if (!drag) return
    const dx = (e.clientX - drag.startX) / scale
    const dy = (e.clientY - drag.startY) / scale
    updateField(drag.index, {
      x: Math.round(Math.min(canvasWidth, Math.max(0, drag.fieldX + dx))),
      y: Math.round(Math.min(canvasHeight, Math.max(0, drag.fieldY + dy))),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, canvasWidth, canvasHeight])

  function onWindowPointerUp() {
    dragState.current = null
    window.removeEventListener("pointermove", onWindowPointerMove)
    window.removeEventListener("pointerup", onWindowPointerUp)
  }

  async function handleSave() {
    if (!name.trim()) { toastError("El nombre de la plantilla es obligatorio"); return }
    if (!backgroundKey) { toastError("Sube una imagen de fondo primero"); return }
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        background_key: backgroundKey,
        canvas_width: canvasWidth,
        canvas_height: canvasHeight,
        fields,
        mode,
        seedbed_id: seedbedId || undefined,
      }
      if (isNew) {
        const res = await api.post("/academy/certificate-templates", payload)
        navigate(`/instructor/certificate-templates/${res.data.data.id}`, { replace: true })
      } else {
        await api.put(`/academy/certificate-templates/${id}`, payload)
      }
    } catch (err) {
      toastError(err)
    } finally {
      setSaving(false)
    }
  }

  async function handlePreview() {
    if (isNew) { toastError("Guarda la plantilla primero para poder previsualizarla"); return }
    setPreviewing(true)
    try {
      const values: Record<string, string> = {}
      fields.forEach((f) => { values[f.key] = f.sampleValue || f.label || f.key })
      const res = await api.post(`/academy/certificate-templates/${id}/preview`, { values }, { responseType: "blob" })
      setPreviewUrl(URL.createObjectURL(res.data))
    } catch (err) {
      toastError(err)
    } finally {
      setPreviewing(false)
    }
  }

  const selectedField = selectedIndex !== null ? fields[selectedIndex] : null

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Programas",
        { label: "Certificados", to: "/instructor/certificates" },
        { label: "Plantillas", to: "/instructor/certificate-templates" },
        isNew ? "Nueva" : name || "Editar",
      ]}
      title={isNew ? "Nueva plantilla de certificado" : `Editar: ${name}`}
      subtitle="Sube una imagen de fondo y arrastra los campos de texto sobre ella"
      tabs={[]}
      metrics={[]}
      actions={
        <div className="flex gap-2">
          <button type="button" onClick={() => navigate("/instructor/certificate-templates")} className={btnSecondary}>
            ← Volver
          </button>
          <button type="button" onClick={() => void handleSave()} disabled={saving} className={btnSave}>
            {saving ? "Guardando..." : "Guardar plantilla"}
          </button>
        </div>
      }
    >
      {loading ? (
        <LoadState />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div>
                <label className={labelCls}>Nombre de la plantilla</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Ej: Certificado semillero 2026" />
              </div>
              <div>
                <label className={labelCls}>Modo de emisión</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setMode("individual")}
                    className={`flex-1 rounded-xl px-3 py-2 text-xs font-medium transition-colors ${mode === "individual" ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"}`}>
                    Individual (uno por persona)
                  </button>
                  <button type="button" onClick={() => setMode("group")}
                    className={`flex-1 rounded-xl px-3 py-2 text-xs font-medium transition-colors ${mode === "group" ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"}`}>
                    Único para todo el grupo
                  </button>
                </div>
              </div>
              <div>
                <label className={labelCls}>Imagen de fondo</label>
                <input type="file" accept="image/*" disabled={uploading}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFileChange(f) }}
                  className="block w-full text-sm text-gray-600 dark:text-gray-400" />
                {uploading && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Subiendo...</p>}
              </div>
            </div>

            {backgroundUrl ? (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                <div
                  ref={canvasRef}
                  className="relative select-none overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600"
                  style={{ width: DISPLAY_MAX_WIDTH, height: displayHeight, backgroundImage: `url(${backgroundUrl})`, backgroundSize: "cover" }}
                >
                  {fields.map((field, i) => {
                    const translateX = field.align === "center" ? "-50%" : field.align === "right" ? "-100%" : "0%"
                    return (
                      <div
                        key={field.key}
                        onPointerDown={(e) => onFieldPointerDown(e, i)}
                        className={`absolute cursor-move whitespace-nowrap rounded px-1 ${
                          selectedIndex === i ? "ring-2 ring-brand-500 bg-brand-500/10" : "hover:bg-black/5"
                        }`}
                        style={{
                          left: field.x * scale,
                          top: field.y * scale - field.fontSize * scale * 0.8,
                          transform: `translateX(${translateX})`,
                          fontSize: Math.max(10, field.fontSize * scale),
                          color: field.color,
                          fontWeight: field.fontWeight || "normal",
                          textTransform: field.textTransform === "uppercase" ? "uppercase" : "none",
                        }}
                      >
                        {field.sampleValue || field.label || field.key}
                      </div>
                    )
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Arrastra cada campo para posicionarlo. Lienzo real: {canvasWidth}×{canvasHeight}px.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-10 text-center">
                <p className="text-gray-500 dark:text-gray-400 text-sm">Sube una imagen de fondo para empezar a posicionar los campos.</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {FIELD_PRESETS.filter((p) => !fields.some((f) => f.key === p.key)).map((p) => (
                <button key={p.key} type="button" onClick={() => addField(p)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400">
                  + {p.label}
                </button>
              ))}
              <button type="button" onClick={addCustomField}
                className="rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50 dark:border-gray-600">
                + Campo personalizado
              </button>
            </div>

            <button type="button" onClick={() => void handlePreview()} disabled={previewing}
              className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-60 dark:bg-gray-700">
              {previewing ? "Generando vista previa..." : "Vista previa"}
            </button>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Propiedades del campo</h3>
            {!selectedField ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">Selecciona un campo en el lienzo para editarlo.</p>
            ) : (
              <div className="space-y-3 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                <div>
                  <label className={labelCls}>Texto de ejemplo</label>
                  <input value={selectedField.sampleValue || ""} onChange={(e) => updateField(selectedIndex!, { sampleValue: e.target.value })} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>X</label>
                    <input type="number" value={selectedField.x} onChange={(e) => updateField(selectedIndex!, { x: Number(e.target.value) })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Y</label>
                    <input type="number" value={selectedField.y} onChange={(e) => updateField(selectedIndex!, { y: Number(e.target.value) })} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Tamaño de fuente</label>
                  <input type="number" min={8} max={300} value={selectedField.fontSize} onChange={(e) => updateField(selectedIndex!, { fontSize: Number(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Color</label>
                  <input type="color" value={selectedField.color} onChange={(e) => updateField(selectedIndex!, { color: e.target.value })} className="h-11 w-full rounded-xl border border-gray-300 dark:border-gray-700" />
                </div>
                <div>
                  <label className={labelCls}>Alineación</label>
                  <div className="flex gap-1">
                    {(["left", "center", "right"] as Align[]).map((a) => (
                      <button key={a} type="button" onClick={() => updateField(selectedIndex!, { align: a })}
                        className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-medium ${selectedField.align === a ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"}`}>
                        {a === "left" ? "Izq." : a === "center" ? "Centro" : "Der."}
                      </button>
                    ))}
                  </div>
                </div>
                <Toggle
                  checked={selectedField.textTransform === "uppercase"}
                  onChange={(v) => updateField(selectedIndex!, { textTransform: v ? "uppercase" : "none" })}
                  label="Mayúsculas"
                />
                <button type="button" onClick={() => removeField(selectedIndex!)}
                  className="w-full rounded-lg border border-error-200 px-3 py-1.5 text-xs font-medium text-error-600 hover:bg-error-50 dark:border-error-900 dark:text-error-400">
                  Eliminar campo
                </button>
              </div>
            )}

            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
              <h4 className="text-xs font-semibold text-gray-500 mb-2">Campos ({fields.length})</h4>
              <div className="space-y-1">
                {fields.map((f, i) => (
                  <button key={f.key} type="button" onClick={() => setSelectedIndex(i)}
                    className={`block w-full rounded px-2 py-1 text-left text-xs ${selectedIndex === i ? "bg-brand-50 text-brand-700 dark:bg-brand-900/20" : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"}`}>
                    {f.label || f.key}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={!!previewUrl} onClose={() => setPreviewUrl(null)} className="max-w-3xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Vista previa</h3>
        {previewUrl && <img src={previewUrl} alt="Vista previa del certificado" className="w-full rounded-lg border border-gray-200 dark:border-gray-700" />}
      </Modal>
    </ModuleLayout>
  )
}
